import { useState } from 'react';
import styled from 'styled-components';
import {
  Flex,
  flexColumn,
  FlexSpace,
  FlexColumn,
  Button,
  LoadingButton,
  TextField,
} from '@elementary/common-ui/components';
import {
  getFirstLettersOfName,
  getAvatarColors,
} from '@elementary/common-ui/utils/common';
import { useAuth, useAuthActions } from '@frontegg/react';
import { Typography, InputLabel } from '@mui/material';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { ACCOUNT_USERS } from '@elementary/common-ui/constants/queryKeys';
import useSnackbar from 'shared/hooks/useSnackbar';
import * as api from 'shared/services/settingsApi';
import { UserForm } from 'shared/types';
import { fontWeight, radius, space } from '@elementary/common-ui/style/utils';
import SettingsSection from './SettingsSection';

const AVATAR_FONT_SIZE = '60px';

const Form = styled.form`
  ${flexColumn};
  gap: ${space('lg')};
  width: 100%;
`;

const Circle = styled(Flex)<{ $name: string }>`
  width: 160px;
  height: 160px;
  min-width: 160px;
  min-height: 160px;
  border-radius: ${radius('capsule')};
  text-align: center;
  justify-content: center;
  align-items: center;
  background: ${({ $name, theme }) => getAvatarColors($name, theme).background};
  color: ${({ $name, theme }) => getAvatarColors($name, theme).text};
`;

const TextInCircle = styled(Typography)`
  color: inherit;
  font-size: ${AVATAR_FONT_SIZE};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
`;

const FieldsContainer = styled(FlexColumn)`
  width: 100%;
  gap: ${space('hugeMinus')};
`;

const UserProfile = () => {
  const { register, handleSubmit } = useForm<UserForm>({});
  const { user } = useAuth() || {};
  const { requestAuthorize } = useAuthActions() || {};
  const { openSnackbar } = useSnackbar();

  const [editMode, setEditMode] = useState<boolean>(false);

  const queryClient = useQueryClient();

  const { mutate, isLoading: submitIsLoading } = useMutation(
    ({ fullName }: UserForm) => api.updateOwnUser(fullName),
    {
      onSuccess: () => {
        requestAuthorize();
        queryClient.invalidateQueries([ACCOUNT_USERS]);
        openSnackbar({ message: 'Personal info changed successfully.' });
      },
    },
  );

  const onSubmit = (formData: UserForm) => {
    if (editMode) {
      setEditMode(!editMode);
      return mutate(formData);
    }
    return setEditMode(!editMode);
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <SettingsSection>
        <FlexSpace sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: fontWeight('semibold') }}>
            Personal info
          </Typography>
          {editMode ? (
            <Flex sx={{ gap: 1 }}>
              <Button variant="outlined" onClick={() => setEditMode(!editMode)}>
                Cancel
              </Button>
              <LoadingButton
                variant="contained"
                type="submit"
                loading={submitIsLoading}
              >
                {editMode ? 'Save' : 'Edit'}
              </LoadingButton>
            </Flex>
          ) : (
            <Button variant="outlined" onClick={() => setEditMode(!editMode)}>
              Edit
            </Button>
          )}
        </FlexSpace>
        <Flex sx={{ gap: 4 }}>
          <Circle $name={user!.id}>
            <TextInCircle>{getFirstLettersOfName(user!.name)}</TextInCircle>
          </Circle>

          <FieldsContainer>
            <FlexColumn>
              <InputLabel shrink>Full Name</InputLabel>
              <TextField
                variant="outlined"
                size="large"
                disabled={!editMode}
                defaultValue={user?.name}
                {...register('fullName', {
                  required: 'This field is required',
                })}
              />
            </FlexColumn>

            <FlexColumn>
              <InputLabel shrink>Email</InputLabel>
              <TextField
                variant="outlined"
                size="large"
                value={user?.email}
                disabled
              />
            </FlexColumn>
          </FieldsContainer>
        </Flex>
      </SettingsSection>
    </Form>
  );
};

export default UserProfile;
